// src/pages/settings/WaterBillSettingsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, Row, Spinner, Table, Badge } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Trash2 } from "react-feather";

import { getPerm } from "../../../utils/perm";
import "../../../css/master-common.css";

import {
  clearWaterbillForm,
  clearWaterbillMsg,
  fetchWaterbillByIdThunk,
  fetchWaterbillListThunk,
  saveWaterbillThunk,
  deleteWaterbillThunk,
} from "../../../store/slices/waterbillSettingsSlice";

const emptySlab = { from_reading: "", to_reading: "", rate_per_unit: "" };
const ITEMS_PER_PAGE = 8;

export default function WaterBillSettingsPage() {
  const dispatch = useDispatch();

  const {
    list,
    formData,
    loadingList,
    loadingOne,
    saving,
    deleting,
    error,
    success,
  } = useSelector((s) => s.waterbillSettings);

  const permsMap = useSelector((s) => s.permissions?.map) || {};
  const perms = getPerm(permsMap, "waterbill_settings") || {};
  const canUpdate = !!perms.can_update;
  const canDelete = !!perms.can_delete;

  const [form, setForm] = useState({
    wbs_id: "",
    water_bill_setting_type: "water",
    mtr_type: "",
    min_ltr: "",
    min_amt: "",
    extra_amt: "",
    slrm: "0",
    status: "active",
    slabs: [],
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  useEffect(() => {
    dispatch(fetchWaterbillListThunk());
  }, [dispatch]);

  useEffect(() => {
    if (!formData) return;

    const settingType = String(formData.water_bill_setting_type || "water").toLowerCase();

    setForm({
      wbs_id: formData.wbs_id || "",
      water_bill_setting_type: settingType,
      mtr_type: formData.mtr_type || "",
      min_ltr: settingType === "slrm" ? "" : (formData.min_ltr ?? ""),
      min_amt: settingType === "slrm" ? "" : (formData.min_amt ?? ""),
      extra_amt: settingType === "slrm" ? "" : (formData.extra_amt ?? ""),
      slrm: settingType === "water" ? "0" : (formData.slrm ?? ""),
      status: formData.status || "active",
      slabs:
        settingType === "slrm"
          ? []
          : Array.isArray(formData.slabs)
          ? formData.slabs
          : [],
    });
  }, [formData]);

  useEffect(() => {
    if (!error) return;
    toast.error(String(error));
    dispatch(clearWaterbillMsg());
    setPendingDeleteId(null);
  }, [error, dispatch]);

  useEffect(() => {
    if (!success) return;
    toast.success(String(success));
    dispatch(clearWaterbillMsg());
    dispatch(fetchWaterbillListThunk());
    setPendingDeleteId(null);
  }, [success, dispatch]);

  const resetForm = () => {
    dispatch(clearWaterbillForm());
    setForm({
      wbs_id: "",
      water_bill_setting_type: "water",
      mtr_type: "",
      min_ltr: "",
      min_amt: "",
      extra_amt: "",
      slrm: "0",
      status: "active",
      slabs: [],
    });
  };

  const addSlab = () =>
    setForm((p) => ({
      ...p,
      slabs: [...(p.slabs || []), { ...emptySlab }],
    }));

  const removeSlab = (idx) =>
    setForm((p) => ({
      ...p,
      slabs: (p.slabs || []).filter((_, i) => i !== idx),
    }));

  const updateSlab = (idx, key, value) => {
    setForm((p) => ({
      ...p,
      slabs: (p.slabs || []).map((s, i) => (i === idx ? { ...s, [key]: value } : s)),
    }));
  };

  const rows = useMemo(() => list || [], [list]);

  const filteredRows = useMemo(() => {
    const q = String(searchTerm || "").trim().toLowerCase();

    return rows.filter((r) => {
      const type = String(r.water_bill_setting_type || "").toLowerCase();
      const matchType = typeFilter === "all" ? true : type === typeFilter;

      if (!q) return matchType;

      const hay =
        `${r.wbs_id} ${r.water_bill_setting_type} ${r.mtr_type} ${r.min_ltr} ${r.min_amt} ${r.extra_amt} ${r.slrm} ${r.status}`
          .toLowerCase()
          .trim();

      return matchType && hay.includes(q);
    });
  }, [rows, searchTerm, typeFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, typeFilter]);

  const totalPages = Math.ceil(filteredRows.length / ITEMS_PER_PAGE) || 1;
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedRows = filteredRows.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const onEdit = (id) => {
    dispatch(fetchWaterbillByIdThunk(id));
  };

  const handleDeleteConfirmed = async (id, toastId) => {
    if (toastId) toast.dismiss(toastId);

    setPendingDeleteId(id);
    const r = await dispatch(deleteWaterbillThunk(id));

    if (deleteWaterbillThunk.rejected.match(r)) {
      setPendingDeleteId(null);
      return;
    }

    if (String(form.wbs_id || "") === String(id)) {
      resetForm();
    }
  };

  const confirmDelete = (id) => {
    if (!canDelete) return toast.error("No Delete permission");

    const toastId = toast.info(
      ({ closeToast }) => (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 10 }}>
            Are you sure you want to delete this setting?
          </div>

          <div className="d-flex gap-2">
            <Button
              size="sm"
              variant="danger"
              onClick={() => {
                closeToast?.();
                handleDeleteConfirmed(id, toastId);
              }}
            >
              Yes, Delete
            </Button>

            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                closeToast?.();
                setPendingDeleteId(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ),
      {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
      }
    );
  };

  const onTypeChange = (t) => {
    const type = String(t || "").toLowerCase();

    setForm((p) => ({
      ...p,
      water_bill_setting_type: type,
      min_ltr: type === "slrm" ? "" : p.min_ltr,
      min_amt: type === "slrm" ? "" : p.min_amt,
      extra_amt: type === "slrm" ? "" : p.extra_amt,
      slrm: type === "water" ? "0" : p.slrm,
      slabs: type === "slrm" ? [] : p.slabs,
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!canUpdate) return toast.error("No Update permission");

    const type = String(form.water_bill_setting_type || "").trim().toLowerCase();
    if (!["water", "slrm"].includes(type)) return toast.error("Setting type required");

    const mtr = String(form.mtr_type || "").trim();
    if (!mtr) return toast.error("Type Name required");

    if (type === "water") {
      if (form.min_ltr === "" || form.min_ltr === null) {
        return toast.error("Minimum Liter required");
      }
      if (form.min_amt === "" || form.min_amt === null) {
        return toast.error("Minimum Amount required");
      }
      if (form.extra_amt === "" || form.extra_amt === null) {
        return toast.error("Extra Amount required");
      }

      for (let i = 0; i < (form.slabs?.length || 0); i++) {
        const s = form.slabs[i];
        if (s.from_reading === "" || s.to_reading === "" || s.rate_per_unit === "") {
          return toast.error(`Slab row ${i + 1} is incomplete`);
        }
      }
    }

    if (type === "slrm" && (form.slrm === "" || form.slrm === null)) {
      return toast.error("SLRM Fees required");
    }

    const payload = {
      ...form,
      water_bill_setting_type: type,
      mtr_type: mtr,
      status: form.status || "active",
      min_ltr: type === "slrm" ? "" : form.min_ltr,
      min_amt: type === "slrm" ? "" : form.min_amt,
      extra_amt: type === "slrm" ? "" : form.extra_amt,
      slrm: type === "water" ? "0" : form.slrm,
      slabs: type === "slrm" ? [] : Array.isArray(form.slabs) ? form.slabs : [],
    };

    const r = await dispatch(saveWaterbillThunk(payload));
    if (saveWaterbillThunk.rejected.match(r)) {
      const msg =
        r?.payload?.message ||
        r?.payload ||
        r?.error?.message ||
        "Failed to save waterbill settings";
      toast.error(String(msg));
    }
  };

  const TypeBadge = ({ v }) => {
    const t = String(v || "").toLowerCase();
    return (
      <Badge bg={t === "water" ? "primary" : "success"} style={{ textTransform: "uppercase" }}>
        {t || "—"}
      </Badge>
    );
  };

  return (
    <div className="container-fluid px-3 py-4 mm-page">
      <Card className="mm-card-soft mb-4">
        <Card.Header style={{ fontWeight: 600 }}>Add / Edit Water Slrm Setting</Card.Header>

        <Card.Body>
          {loadingOne ? (
            <div className="text-center p-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <Form onSubmit={onSubmit}>
              <Row className="g-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label style={{ fontWeight: 500 }}>
                      Setting Type <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      className="mm-select"
                      value={form.water_bill_setting_type}
                      onChange={(e) => onTypeChange(e.target.value)}
                      required
                      disabled={saving || deleting}
                    >
                      <option value="water">WATER</option>
                      <option value="slrm">SLRM</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group>
                    <Form.Label style={{ fontWeight: 500 }}>
                      Type Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      className="mm-input"
                      value={form.mtr_type}
                      onChange={(e) => setForm((p) => ({ ...p, mtr_type: e.target.value }))}
                      placeholder="Ex: H1 / C1 / Monthly / Door-to-door"
                      autoComplete="off"
                      required
                      disabled={saving || deleting}
                    />
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group>
                    <Form.Label style={{ fontWeight: 500 }}>Status</Form.Label>
                    <Form.Select
                      className="mm-select"
                      value={form.status}
                      onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                      required
                      disabled={saving || deleting}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group>
                    <Form.Label style={{ fontWeight: 500 }}>
                      SLRM Fees{" "}
                      {form.water_bill_setting_type === "slrm" && (
                        <span className="text-danger">*</span>
                      )}
                    </Form.Label>
                    <Form.Control
                      className="mm-input"
                      type="number"
                      step="0.01"
                      value={form.slrm}
                      onChange={(e) => setForm((p) => ({ ...p, slrm: e.target.value }))}
                      placeholder="Enter SLRM fees"
                      required={form.water_bill_setting_type === "slrm"}
                      disabled={saving || deleting}
                    />
                  </Form.Group>
                </Col>

                {form.water_bill_setting_type === "water" && (
                  <>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label style={{ fontWeight: 500 }}>
                          Minimum Liter <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          className="mm-input"
                          type="number"
                          step="0.01"
                          value={form.min_ltr}
                          onChange={(e) => setForm((p) => ({ ...p, min_ltr: e.target.value }))}
                          placeholder="Enter minimum liter"
                          required={form.water_bill_setting_type === "water"}
                          disabled={saving || deleting}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group>
                        <Form.Label style={{ fontWeight: 500 }}>
                          Minimum Amount <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          className="mm-input"
                          type="number"
                          step="0.01"
                          value={form.min_amt}
                          onChange={(e) => setForm((p) => ({ ...p, min_amt: e.target.value }))}
                          placeholder="Enter minimum amount"
                          required={form.water_bill_setting_type === "water"}
                          disabled={saving || deleting}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group>
                        <Form.Label style={{ fontWeight: 500 }}>
                          Extra Amount per Unit <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          className="mm-input"
                          type="number"
                          step="0.01"
                          value={form.extra_amt}
                          onChange={(e) => setForm((p) => ({ ...p, extra_amt: e.target.value }))}
                          placeholder="Enter extra amount"
                          required={form.water_bill_setting_type === "water"}
                          disabled={saving || deleting}
                        />
                      </Form.Group>
                    </Col>
                  </>
                )}
              </Row>

              {form.water_bill_setting_type === "water" && (
                <>
                  <div className="mt-4 d-flex justify-content-between align-items-center">
                    <div style={{ fontWeight: 600 }}>Slabs</div>
                    <Button
                      className="mm-primary-btn"
                      size="sm"
                      type="button"
                      onClick={addSlab}
                      disabled={saving || deleting}
                    >
                      Add Slab
                    </Button>
                  </div>

                  {form.slabs.length === 0 ? (
                    <div className="text-muted small mt-2">No slab rows added</div>
                  ) : (
                    <div className="mt-2">
                      {form.slabs.map((s, idx) => (
                        <Row className="g-2 mb-2" key={idx}>
                          <Col md={3}>
                            <Form.Control
                              className="mm-input"
                              type="number"
                              value={s.from_reading}
                              placeholder="From"
                              onChange={(e) => updateSlab(idx, "from_reading", e.target.value)}
                              required
                              disabled={saving || deleting}
                            />
                          </Col>
                          <Col md={3}>
                            <Form.Control
                              className="mm-input"
                              type="number"
                              value={s.to_reading}
                              placeholder="To"
                              onChange={(e) => updateSlab(idx, "to_reading", e.target.value)}
                              required
                              disabled={saving || deleting}
                            />
                          </Col>
                          <Col md={4}>
                            <Form.Control
                              className="mm-input"
                              type="number"
                              value={s.rate_per_unit}
                              placeholder="Rate per unit"
                              onChange={(e) => updateSlab(idx, "rate_per_unit", e.target.value)}
                              required
                              disabled={saving || deleting}
                            />
                          </Col>
                          <Col md={2}>
                            <Button
                              variant="light"
                              size="sm"
                              type="button"
                              onClick={() => removeSlab(idx)}
                              className="mm-icon-btn danger"
                              title="Remove Slab"
                              disabled={saving || deleting}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </Col>
                        </Row>
                      ))}
                    </div>
                  )}
                </>
              )}

              <div className="d-flex justify-content-end gap-2 mt-4">
                <Button
                  variant="light"
                  type="button"
                  className="mm-cancel-btn"
                  onClick={resetForm}
                  disabled={saving || deleting}
                >
                  Clear
                </Button>

                <Button
                  className="mm-primary-btn"
                  type="submit"
                  disabled={!canUpdate || saving || deleting}
                >
                  {saving ? "Saving..." : form.wbs_id ? "Update" : "Save"}
                </Button>
              </div>

              {!canUpdate ? (
                <div style={{ marginTop: 10, color: "#ef4444", fontWeight: 500 }}>
                  No Update permission
                </div>
              ) : null}
            </Form>
          )}
        </Card.Body>
      </Card>

      <Card className="mm-card">
        <Card.Header style={{ fontWeight: 600 }}>Bill Settings List</Card.Header>

        <Card.Body>
          <Row className="mb-3 g-2 align-items-center">
            <Col md={3}>
              <Form.Select
                className="mm-select"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="water">Water</option>
                <option value="slrm">SLRM</option>
              </Form.Select>
            </Col>

            <Col md={5}>
              <Form.Control
                className="mm-input"
                placeholder="Search id / type / name / status..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>

            <Col md={4} className="text-end text-muted small">
              Showing {filteredRows.length === 0 ? 0 : startIndex + 1}-
              {Math.min(startIndex + ITEMS_PER_PAGE, filteredRows.length)} of{" "}
              <b>{filteredRows.length}</b>
            </Col>
          </Row>

          {loadingList ? (
            <div className="text-center p-4">
              <Spinner animation="border" />
              <div className="text-muted small mt-2">Loading list...</div>
            </div>
          ) : (
            <>
              <div className="mm-table-wrap">
                <Table hover bordered responsive size="sm" className="mm-table mb-0">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Type</th>
                      <th>Type Name</th>
                      <th>Min Ltr</th>
                      <th>Min Amt</th>
                      <th>Extra</th>
                      <th>SLRM Fees</th>
                      <th>Status</th>
                      <th style={{ width: 160 }}>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedRows.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center text-muted">
                          {searchTerm || typeFilter !== "all" ? "No results found" : "No records"}
                        </td>
                      </tr>
                    ) : (
                      paginatedRows.map((r) => (
                        <tr key={r.wbs_id}>
                          <td>{r.wbs_id}</td>
                          <td>
                            <TypeBadge v={r.water_bill_setting_type} />
                          </td>
                          <td>{r.mtr_type || "—"}</td>
                          <td>{r.min_ltr ?? "—"}</td>
                          <td>{r.min_amt ?? "—"}</td>
                          <td>{r.extra_amt ?? "—"}</td>
                          <td>{r.slrm ?? "—"}</td>
                          <td style={{ textTransform: "capitalize" }}>{r.status || "—"}</td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => onEdit(r.wbs_id)}
                                disabled={!canUpdate || saving || deleting}
                              >
                                Edit
                              </Button>

                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => confirmDelete(r.wbs_id)}
                                disabled={
                                  !canDelete ||
                                  saving ||
                                  deleting ||
                                  String(pendingDeleteId || "") === String(r.wbs_id)
                                }
                              >
                                {String(pendingDeleteId || "") === String(r.wbs_id) || deleting
                                  ? "Deleting..."
                                  : "Delete"}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {totalPages > 1 ? (
                <div className="d-flex justify-content-center mt-3 gap-3">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  >
                    Prev
                  </Button>

                  <span style={{ fontWeight: 700 }}>
                    Page {currentPage} of {totalPages}
                  </span>

                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}